import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import { extractErrorMessage } from "../../api/client";
import {
  getWorkflowManifest,
  WorkflowDetail,
  WorkflowManifest,
} from "../../api/workflows";
import { renderManifestPdf } from "./manifestRender";

// Workflow manifest panel — renders the pre-aggregated counters the
// C# exporter dropped at the output prefix. Shows totals, time range,
// top stations, top categories, daily distribution, and (for multi-
// month batches) a per-month breakdown. Offers a "Download (PDF)"
// that renders the same data via jspdf in TVEyes brand chrome.
//
// Hidden for non-export workflows + workflows not yet succeeded —
// no manifest exists until the export emits one.

interface Props {
  wf: WorkflowDetail;
}

const EXPORT_KINDS = new Set(["batch_export", "month_export", "year_export"]);

function fmtNumber(n: number): string {
  return n.toLocaleString();
}

function fmtHoursFromSeconds(sec: number): string {
  const hours = sec / 3600;
  if (hours >= 1000) return `${Math.round(hours).toLocaleString()} hr`;
  if (hours >= 10) return `${hours.toFixed(1)} hr`;
  return `${hours.toFixed(2)} hr`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function ManifestPanel({ wf }: Props) {
  const [manifest, setManifest] = useState<WorkflowManifest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Skip the manifest fetch once the reaper has wiped the output —
  // the sidecar lived at the output prefix, so there's nothing to
  // render and the GET would 404. ``output_deleted_at`` is the
  // operator's authoritative signal that the prefix is gone.
  const eligible =
    EXPORT_KINDS.has(wf.kind) &&
    wf.status === "succeeded" &&
    !wf.output_deleted_at;

  // Only fetch once the workflow succeeded — pre-success there's no
  // manifest written to S3 yet. Refresh on workflow id changes so a
  // user navigating directly to ExportDetail sees the right data.
  useEffect(() => {
    if (!eligible) {
      setManifest(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getWorkflowManifest(wf.id)
      .then((m) => {
        if (!cancelled) setManifest(m);
      })
      .catch((err) => {
        if (!cancelled) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wf.id, eligible]);

  const handleDownloadPdf = useCallback(() => {
    if (!manifest) return;
    const doc = renderManifestPdf(manifest);
    doc.save(`paim-manifest-${wf.id.slice(0, 8)}.pdf`);
  }, [manifest, wf.id]);

  const handleDownloadJson = useCallback(() => {
    if (!manifest) return;
    const blob = new Blob([JSON.stringify(manifest, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paim-manifest-${wf.id.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [manifest, wf.id]);

  if (!eligible) return null;
  if (loading && !manifest) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Manifest
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="caption">fetching…</Typography>
        </Stack>
      </Paper>
    );
  }
  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Manifest
        </Typography>
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      </Paper>
    );
  }
  if (!manifest) return null;

  const unit = manifest.output.unit || "record";

  const showPagesTile =
    manifest.totals.total !== manifest.totals.total_page_count;
  const showTranscriptTile = manifest.totals.total_transcript_chars > 0;

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="overline" color="text.secondary">
          Manifest
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={handleDownloadJson}>
            JSON
          </Button>
          <Button size="small" variant="contained" onClick={handleDownloadPdf}>
            PDF
          </Button>
        </Stack>
      </Stack>

      {/* Headline — the dominant total + scope. Big number first;
          time range as a small chip when both bounds are known.
          Filter / Lucene queries get their own code blocks below
          since they can be arbitrarily long. */}
      <Box sx={{ textAlign: "center", py: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textTransform: "uppercase", letterSpacing: 1 }}
        >
          Total {unit}s matched
        </Typography>
        <Typography
          variant="h3"
          sx={{ fontWeight: 700, lineHeight: 1, mt: 0.5, mb: 1 }}
        >
          {fmtNumber(manifest.totals.total)}
        </Typography>
        {(() => {
          const min = manifest.time_range?.min_start_utc;
          const max = manifest.time_range?.max_end_utc;
          let label: string | null = null;
          if (min && max) label = `${min} → ${max}`;
          else if (min) label = `from ${min}`;
          else if (max) label = `until ${max}`;
          if (!label) return null;
          return (
            <Chip
              size="small"
              variant="outlined"
              icon={<ScheduleOutlinedIcon />}
              label={label}
            />
          );
        })()}
      </Box>

      {/* Filter + Lucene predicates intentionally NOT shown here —
          the Job Configuration section at the top of the export
          detail page already surfaces them. Duplicating would
          push the metric cards below the fold. */}

      {/* Secondary metric cards — three to five depending on whether
          pages/transcript add new information beyond the headline. */}
      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        rowGap={2}
        sx={{
          mt: 1,
          mb: 3,
          pt: 2,
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <MetricCard
          label="Stations"
          value={fmtNumber(manifest.totals.distinct_stations_seen)}
          detail="distinct sources seen"
        />
        {showPagesTile && (
          <MetricCard
            label="Pages"
            value={fmtNumber(manifest.totals.total_page_count)}
            detail={`grouped into ${fmtNumber(manifest.totals.total)} ${unit}s`}
          />
        )}
        <MetricCard
          label="Content captured"
          value={fmtHoursFromSeconds(manifest.totals.total_duration_sec)}
          detail="total program runtime"
        />
        {showTranscriptTile && (
          <MetricCard
            label="Transcript text"
            value={fmtBytes(manifest.totals.total_transcript_chars)}
            detail="searchable text body"
          />
        )}
      </Stack>

      {/* Top stations + categories side-by-side */}
      <Stack direction="row" spacing={4} sx={{ mb: 2 }} flexWrap="wrap" rowGap={2}>
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Top stations
          </Typography>
          <RankList
            rows={manifest.by_station.slice(0, 10).map((r) => ({
              name: r.source_name,
              count: r.count,
            }))}
          />
        </Box>
        <Box sx={{ minWidth: 240, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Top categories
          </Typography>
          <RankList
            rows={manifest.by_category.slice(0, 10).map((r) => ({
              name: r.category,
              count: r.count,
            }))}
          />
        </Box>
      </Stack>

      {manifest.months.length > 1 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Per-month breakdown
          </Typography>
          {(() => {
            // Hide columns whose data is uninformative across the
            // whole table: ``Pages`` is identical to ``<unit>s`` for
            // ndjson exports, and ``Transcript`` is always 0 B for
            // every sink that doesn't accumulate transcript_chars
            // (everything except condensed-json today).
            const showPages = manifest.months.some(
              (me) =>
                me.manifest &&
                me.manifest.matched.total !==
                  me.manifest.matched.total_page_count,
            );
            const showTranscript = manifest.months.some(
              (me) =>
                me.manifest &&
                me.manifest.matched.total_transcript_chars > 0,
            );
            const visibleCols = 1 + (showPages ? 1 : 0) + 1 + (showTranscript ? 1 : 0);
            return (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Month</TableCell>
                    <TableCell align="right">{unit}s</TableCell>
                    {showPages && <TableCell align="right">Pages</TableCell>}
                    <TableCell align="right">Hours</TableCell>
                    {showTranscript && (
                      <TableCell align="right">Transcript</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manifest.months.map((me) => (
                    <TableRow key={me.month ?? "(workflow)"}>
                      <TableCell>{me.month ?? "(workflow)"}</TableCell>
                      {me.manifest ? (
                        <>
                          <TableCell align="right">
                            {fmtNumber(me.manifest.matched.total)}
                          </TableCell>
                          {showPages && (
                            <TableCell align="right">
                              {fmtNumber(me.manifest.matched.total_page_count)}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            {fmtHoursFromSeconds(
                              me.manifest.matched.total_duration_sec,
                            )}
                          </TableCell>
                          {showTranscript && (
                            <TableCell align="right">
                              {fmtBytes(
                                me.manifest.matched.total_transcript_chars,
                              )}
                            </TableCell>
                          )}
                        </>
                      ) : (
                        <TableCell
                          colSpan={visibleCols - 1}
                          sx={{ color: "text.secondary" }}
                        >
                          manifest not found
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
          {manifest.totals.months_empty > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              {manifest.totals.months_empty} month
              {manifest.totals.months_empty === 1 ? "" : "s"} returned zero
              matches.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <Box
      sx={{
        flex: "1 1 180px",
        minWidth: 160,
        p: 1.5,
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.default",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2, mt: 0.5 }}>
        {value}
      </Typography>
      {detail && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.5 }}
        >
          {detail}
        </Typography>
      )}
    </Box>
  );
}

function RankList({ rows }: { rows: { name: string; count: number }[] }) {
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        (none)
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25}>
      {rows.map((r) => (
        <Stack
          key={r.name}
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <Typography variant="body2" sx={{ mr: 1 }}>
            {r.name}
          </Typography>
          <Chip
            size="small"
            label={r.count.toLocaleString()}
            sx={{ height: 18, fontSize: 11 }}
          />
        </Stack>
      ))}
    </Stack>
  );
}
